<!-- eslint-disable vue/no-mutating-props -->
<script>
import ContactItem from './ContactItem.vue';
import PlatformContactsTable from './PlatformContactsTable.vue';
import WppConnectAPI from 'dashboard/routes/dashboard/settings/inbox/channels/WppConnectAPI';
import CommonWhatsappAPI from 'dashboard/routes/dashboard/settings/inbox/channels/commonWhatsapp/api';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useManager } from 'dashboard/composables/useManager';
import { mapGetters } from 'vuex';

export default {
  components: {
    ContactItem,
    PlatformContactsTable,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    inbox: {
      type: Object,
      default: () => {},
    },
    conversation: {
      type: Object,
      default: () => {},
    },
    currentContact: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    const { isAdmin } = useAdmin();
    const { isManager } = useManager();
    return {
      isAdmin,
      isManager,
    };
  },
  data() {
    return {
      isUserAdmin: false,
      isLoading: false,
      groupName: '',
      groupDescription: '',
      groupOnlyAdminMessage: false,
      groupImg: '',
      groupInviteLink: '',
      contacts: [],
      showAvailableContacts: false,
      showExitConfirmation: false,
      imgText: '',
    };
  },
  computed: {
    ...mapGetters({
      platformContacts: 'contacts/getContacts',
    }),
    isHigherPermUser() {
      return this.isManager || this.isAdmin;
    },
    isCodeChat() {
      return this.inbox.player === 'codechat';
    },
  },
  mounted() {
    this.getGroupInfo();
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    async getGroupMembers(data = {}) {
      this.isLoading = true;
      if (!this.isCodeChat) {
        const { data: reqData } = await this.getAPI().getGroupMembers(
          this.currentContact.id,
          this.inbox.phone_number
        );
        if (!reqData || !reqData.admins || !reqData.members) {
          useAlert(
            reqData.message ||
              this.$t('CHAT_LIST.CONFIG_GROUP.FAILED_TO_FETCH_INFORMATION')
          );
          return;
        }
        data = reqData;
      }

      const members = this.getAPI().extractGroupParticipantsInfoFromJSON(
        data,
        this.inbox.phone_number
      );

      members.forEach(member => {
        const foundInPlatform = this.platformContacts.find(
          contact => contact.phone_number === '+' + member.phone
        );
        if (foundInPlatform) member.inPlatform = foundInPlatform;
      });

      const currentUser = members.find(member => member.isMe);
      this.isUserAdmin = currentUser.isAdm && this.isHigherPermUser;

      this.contacts = [
        ...members.sort((a, b) => {
          if (a.isAdm && !b.isAdm) {
            return -1; // a vem primeiro
          }
          if (!a.isAdm && b.isAdm) {
            return 1; // b vem primeiro
          }
          return 0; // ordem não alterada
        }),
      ];
      this.isLoading = false;
    },
    getAPI() {
      return this.isCodeChat ? CommonWhatsappAPI : WppConnectAPI;
    },
    async getGroupInfo() {
      this.isLoading = true;

      try {
        const { data } = await this.getAPI().getGroupInfo(
          this.currentContact.id,
          this.inbox.phone_number
        );

        if (data && data.group_info) {
          const { group_info } = data;

          const { name, description, picture, inviteLink } =
            this.getAPI().extractGroupInfoFromJSON(group_info);

          this.groupName = name;
          this.groupDescription = description;
          this.groupImg = picture;
          this.groupInviteLink = inviteLink;

          // Atualizando nome do grupo caso já seja diferente (a partir da $store no vue)
          if (this.groupName !== this.currentContact.name)
            this.$store.dispatch('contacts/update', {
              id: this.currentContact.id,
              name: this.groupName,
            });

          this.getGroupMembers(group_info);
        } else {
          useAlert(
            this.$t('CHAT_LIST.CONFIG_GROUP.FAILED_TO_FETCH_GROUP_INFORMATION')
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        useAlert(
          this.$t('CHAT_LIST.CONFIG_GROUP.FAILED_TO_FETCH_GROUP_INFORMATION')
        );
      }
    },
    handleNameChange($event) {
      const { value } = $event.target;
      if (!value) {
        useAlert(this.$t('CHAT_LIST.CONFIG_GROUP.FILL_IN_THE_FIELD'));
        return;
      }
      this.updateGroupConfig('name', value);
    },
    handleDescriptionChange($event) {
      const { value } = $event.target;
      if (!value) {
        useAlert(this.$t('CHAT_LIST.CONFIG_GROUP.FILL_IN_THE_FIELD'));
        return;
      }
      this.updateGroupConfig('description', value);
    },
    async updateGroupConfig(type, value) {
      this.isLoading = false;
      const request =
        type === 'image'
          ? this.getAPI().updateGroupImage(
              this.currentContact.id,
              this.inbox.phone_number,
              value
            )
          : this.getAPI().updateGroupConfig(
              this.currentContact.id,
              this.inbox.phone_number,
              {
                type,
                value,
              }
            );
      const { data } = await request;
      if (!data || !data.success) {
        useAlert(
          data.message || this.$t('CHAT_LIST.CONFIG_GROUP.FAILED_TO_UPDATE')
        );
        return;
      }
      useAlert(this.$t('CHAT_LIST.CONFIG_GROUP.SUCCESSFULLY_UPDATE'));
      this.getGroupInfo();
    },
    async copyInviteLinkToClipboard() {
      await navigator.clipboard.writeText(this.groupInviteLink);
      if (await navigator.clipboard.readText())
        useAlert(this.$t('CHAT_LIST.CONFIG_GROUP.COPIED_SUCCESSFULLY'));
    },
    uploadImage($event) {
      const { files } = $event.target;
      if (!files && !files[0]) return;
      const imgSrc = URL.createObjectURL(files[0]);
      const img = new Image();
      const popUpMessage = msg => useAlert(msg);
      const updateImg = () => this.updateGroupConfig('image', files[0]);
      // eslint-disable-next-line func-names
      img.onload = function () {
        URL.revokeObjectURL(imgSrc);
        if (this.width !== this.height) {
          popUpMessage(
            this.$t('CHAT_LIST.CONFIG_GROUP.IMAGE_MUST_HAVE_EQUAL_DIMENSIONS')
          );
          return;
        }
        if (this.width > 1024) {
          popUpMessage(this.$t('CHAT_LIST.CONFIG_GROUP.IMAGE_MUST_BE_1024'));
          return;
        }
        updateImg();
      };
      img.src = imgSrc;
    },
    async exitGroup(confirmation = false) {
      this.showExitConfirmation = false;
      if (!confirmation) return;

      this.isLoading = true;
      const { data } = await this.getAPI().leaveGroup(
        this.currentContact.id,
        this.inbox.phone_number,
        this.inbox.id
      );
      if (data && (data.result || data.success)) {
        this.$store.dispatch(
          'setConversationCanReplyFalse',
          this.conversation.id
        );
        this.onClose();
      }
    },
    selectImage() {
      this.$refs.inputImage.click();
    },
  },
};
</script>

<template>
  <woot-modal
    size="medium"
    :show="show"
    :on-close="onClose"
    @update:show="val => (show = val)"
  >
    <div class="column content-box">
      <woot-modal-header
        :header-title="$t('CHAT_LIST.CONFIG_GROUP.GROUP_DETAILS')"
      />
      <div class="main-container">
        <div
          class="pic-container"
          @click="isUserAdmin ? selectImage() : null"
          @mouseover="
            isUserAdmin
              ? (imgText = $t('CHAT_LIST.CONFIG_GROUP.CHANGE_IMAGE'))
              : null
          "
          @mouseout="isUserAdmin ? (imgText = '') : null"
        >
          <img v-if="groupImg && !imgText" :src="groupImg" alt="group-image" />
          <div
            v-if="!groupImg || imgText"
            class="w-50 h-50 cursor-pointer rounded-full bg-gray-500 text-white flex justify-center items-center"
            v-html="
              imgText ? imgText : $t('CHAT_LIST.CONFIG_GROUP.IMAGE_NOT_FOUND')
            "
          />
          <div class="hidden">
            <input
              ref="inputImage"
              :disabled="isLoading || !groupName"
              type="file"
              accept="image/*"
              @change="uploadImage"
            />
          </div>
        </div>

        <div class="name-container w-80">
          <input
            v-model="groupName"
            :disabled="!isUserAdmin || isLoading || isCodeChat"
            type="text"
            class="input-group-field"
            @change="handleNameChange"
          />
        </div>

        <div
          v-if="!isCodeChat || (isCodeChat && groupDescription)"
          class="description-container w-80"
        >
          <textarea
            v-model="groupDescription"
            :disabled="!isUserAdmin || isLoading || isCodeChat"
            rows="3"
            @change="handleDescriptionChange"
          />
        </div>

        <div v-if="isUserAdmin" class="add-participants">
          <woot-button
            color-scheme="primary"
            icon="person"
            :disabled="isLoading"
            @click.prevent="showAvailableContacts = true"
          >
            Adicionar Participante
          </woot-button>
          <PlatformContactsTable
            :available-contacts="platformContacts"
            :is-code-chat="isCodeChat"
            :current-members="contacts"
            :show="showAvailableContacts"
            :current-contact="currentContact"
            :inbox="inbox"
            @added-new-contacts="
              showAvailableContacts = false;
              getGroupInfo();
            "
            @on-close="showAvailableContacts = false"
          />
          <woot-button
            color-scheme="primary"
            icon="share"
            :disabled="!groupInviteLink || isLoading"
            @click.prevent="copyInviteLinkToClipboard"
          >
            Link de Convite
          </woot-button>
        </div>

        <div class="participants-list">
          <ContactItem
            v-for="contact in contacts"
            :key="contact.id.user"
            :is-code-chat="isCodeChat"
            :contact-info="contact"
            :is-current-user-adm="isUserAdmin"
            :current-contact="currentContact"
            :inbox="inbox"
            :is-loading="isLoading"
            @update-contact="getGroupInfo"
            @on-loading="isLoading = true"
          />
          <div v-if="[0, 1].includes(contacts.length) && !isLoading">
            Não foram encontrados outros participantes no grupo
          </div>
        </div>
      </div>
    </div>
    <woot-delete-modal
      v-model:show="showExitConfirmation"
      :on-close="
        () => {
          exitGroup(false);
        }
      "
      :on-confirm="
        () => {
          exitGroup(true);
        }
      "
      :title="$t('CHAT_LIST.CONFIG_GROUP.TITLE')"
      :message="$t('CHAT_LIST.CONFIG_GROUP.MESSAGE')"
      :confirm-text="$t('CHAT_LIST.CONFIG_GROUP.CONFIRM')"
      :reject-text="$t('CHAT_LIST.CONFIG_GROUP.REJECT')"
    />
    <div class="modal-footer delete-item">
      <button
        class="button clear alert expanded"
        :disabled="isLoading"
        @click.prevent="showExitConfirmation = true"
      >
        {{ $t('CHAT_LIST.CONFIG_GROUP.LEFT') }}
      </button>
    </div>
  </woot-modal>
</template>

<style lang="scss">
.main-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  cursor: default;
}
.pic-container {
  max-width: 200px;
  img {
    border-radius: 50%;
  }
}
.w-80 {
  width: 80%;
}
.participants-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
  max-height: 30vh;
}
</style>
